import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridColumnHeaderParams, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Tooltip from 'components/tooltip';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Info } from 'lucide-react';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getWaterQuality, getWaterQualityType, putWaterQuality } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { UpdateWaterQualityInput } from 'views/production-systems/features/registration-log/types/update-water-quality-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { AmPm } from 'views/production-systems/types/water-quality.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';
import { hasValueError } from '../../../utils/water-quality-utils';

type EditWaterQualityProps = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type ParamTypes = {
  globalSiteId: string;
};

type Row = {
  pondId: string;
  pondName: string;
  cycleId: number;
  zoneName: string;
  cycleName: string;
  previousMw: number;
  previousQty: number;
  previousBm: number;
  waterQualityTypeId: number;
  value: number;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.waterQuality';

const EditWaterQuality: React.FC<EditWaterQualityProps> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, lockPonds, releasePonds } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  const [rows, setRows] = useState<readonly Row[]>([]);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [dayTime, setDayTime] = useState<AmPm>(AmPm.AM);

  //#region FETCH DATA

  const { data: waterQuality, isLoading } = useGet(
    ['getWaterQuality', globalSiteId, logId, eventNr],
    ({ queryKey }) => getWaterQuality(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (!waterQuality) return;

    if (canEdit) lockPonds(waterQuality.cycles.map((cycle) => cycle.pondId));
    if (waterQuality.cycles.length > 0) {
      setDayTime(waterQuality.cycles[0].amPm);
    }
  }, [canEdit, lockPonds, waterQuality]);

  useEffect(() => {
    if (!!waterQuality) {
      let rows: readonly any[] = waterQuality.cycles.map((cycle) => {
        return {
          pondName: cycle.pondName,
          pondId: cycle.pondId,
          cycleName: cycle.cycleName,
          cycleId: cycle.cycleId,
          zoneName: cycle.zoneName,
          previousMw: cycle.previousMw,
          previousQty: cycle.previousQty,
          previousBm: calculateBiomass(cycle.previousMw, cycle.previousQty),
          waterQualityTypeId: cycle.waterQualityTypeId,
          value: cycle.value,
        };
      });

      setRows(rows);
    }
  }, [waterQuality]);

  const { data: waterQualityTypes } = useGet(['getWaterQualityType', params.globalSiteId], ({ queryKey }) =>
    getWaterQualityType(String(queryKey[1]))
  );

  //#endregion FETCH DATA

  //#region Data Grid

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow };
    setRows(
      rows.map((row) => {
        if (row.pondId === newRow.pondId) {
          return updatedRow;
        }

        return { ...row, waterQualityTypeId: !row.waterQualityTypeId ? updatedRow.waterQualityTypeId : row.waterQualityTypeId }; // better way to do this?
      })
    );
    return updatedRow;
  };

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'pondName',
          flex: 2,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.destinationPond` }),
          editable: false,
          renderCell: ({ row }) => {
            return (
              <Box display={'flex'} gap={2} alignItems={'center'} width={'100%'}>
                {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
                <Box overflow={'hidden'} textOverflow={'ellipsis'} title={row.pondName}>
                  {row.pondName}
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'cycleName',
          flex: 1,
          editable: false,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.cycleName` }),
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'zoneName',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.zone` }),
          type: 'string',
          editable: false,
          align: 'left',
          headerAlign: 'left',
          flex: 1,
        },
        {
          field: 'previousQty',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.number` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: false,
          flex: 1,
        },
        {
          field: 'previousMw',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.meanWeight` }),
          sortable: false,
          editable: false,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          flex: 1,
        },
        {
          field: 'previousBm',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.biomass` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: false,
          flex: 1,
        },
        {
          field: 'waterQualityTypeId',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.edit.waterQualityType` }),
          type: 'singleSelect',
          align: 'left',
          headerAlign: 'left',
          valueOptions: waterQualityTypes?.map((type) => ({ value: type.id, label: type.name })),
          editable: !readOnly,
          flex: 1,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const hasError = !params.props.value;
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-waterQualityTypeId`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'value',
          renderHeader: (params: GridColumnHeaderParams) => {
            return (
              <Box display={'inline-flex'} alignItems={'center'}>
                <FormattedMessage id={`${PAGE_NAME_ID}.add.value`} />
                <span>
                  <Tooltip
                    title={
                      <Box display={'flex'} flexDirection={'column'} justifyContent={'space-around'} gap={4}>
                        <Box display={'flex'} flexDirection={'row'}>
                          {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.1' })}:
                          {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '20' })}
                        </Box>
                        <Box display={'flex'} flexDirection={'row'}>
                          {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.2' })}:
                          {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '120' })}
                        </Box>
                        <Box display={'flex'} flexDirection={'row'}>
                          {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.3' })}:
                          {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '15', upper: '45' })}
                        </Box>
                        <Box display={'flex'} flexDirection={'row'}>
                          {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.4' })}:
                          {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '60' })}
                        </Box>
                        <Box display={'flex'} flexDirection={'row'}>
                          {intl.formatMessage({ id: 'productionSystems.farmOverview.waterQuality.types.5' })}:
                          {' ' + intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '14' })}
                        </Box>
                      </Box>
                    }>
                    <Info size={18} style={{ marginRight: 4, marginLeft: 4 }} />
                  </Tooltip>
                </span>
              </Box>
            );
          },
          cellClassName: (params) => {
            const { value, waterQualityTypeId } = params.row;
            const hasError = value === null || hasValueError(value, waterQualityTypeId);

            return hasError ? 'cell--warning' : '';
          },
          flex: 1,
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          type: 'number',
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const { value } = params.props;
            const waterQualityTypeId = params.row.waterQualityTypeId;
            const error = value === null || hasValueError(value, waterQualityTypeId);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-value`]: error });
            return { ...params.props, error };
          },
        },
      ] as GridColDef[],
    [readOnly, waterQualityTypes]
  );

  const summary: SummaryItem[] = useMemo(() => {
    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.previousMw, curr.previousQty);

        return {
          previousQty: curr.previousQty + prev.previousQty,
          previousBm: biomass + prev.previousBm,
          previousMw: calculateMeanWeight(biomass, curr.previousQty) + prev.previousMw,
        };
      },
      {
        previousMw: 0,
        previousQty: 0,
        previousBm: 0,
      }
    );

    return [
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.previousQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.previousMw,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.previousBm,
        type: 'number',
        hidden: false,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
    ];
  }, [rows]);

  //#endregion Data Grid

  //#region HANDLERS

  const handleSubmit = () => {
    let d: UpdateWaterQualityInput = {
      cycles: rows.map((row) => ({
        cycleId: row.cycleId,
        waterQualityTypeId: row.waterQualityTypeId,
        value: row.value,
        pondId: row.pondId,
        amPm: dayTime,
      })),
    };

    updateWaterQuality.mutate({ globalSiteId, data: d });
  };

  //#endregion HANDLERS

  //#region UPDATE DATA

  const updateWaterQuality = usePost<AxiosResponse, { globalSiteId: string; data: UpdateWaterQualityInput }>(
    (request) => putWaterQuality(params.globalSiteId, logId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      waterQuality && releasePonds(waterQuality.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion UPDATE DATA

  //#region HELPERS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);

  const disableSubmit = useMemo(() => {
    return rows.some((row) => !row.waterQualityTypeId || hasValueError(row.value, row.waterQualityTypeId));
  }, [rows]);

  //#endregion HELPERS

  return isLoading ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <Box display={'flex'} flexDirection={'column'}>
        <RegistrationHeader dateCreated={dateCreated} />
        <Box display="flex" justifyContent="end" alignItems={'center'} mb={2}>
          <Select
            dark
            label={''}
            hiddenLabel
            value={dayTime}
            size="small"
            onChange={(event) => {
              setDayTime(event.target.value as AmPm);
            }}>
            {Object.entries(AmPm)?.map((time) => (
              <RadioSelectItem key={time[0]} value={time[1]}>
                {time[0]}
              </RadioSelectItem>
            ))}
          </Select>
        </Box>
        <DataGrid
          columns={columns}
          rows={rows}
          apiRef={apiRef}
          getRowId={(row) => row.pondId}
          processRowUpdate={processRowUpdate}
          showToolbar
          slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: `${intl.formatMessage({
                  id: 'productionSystems.registrationLog.registrationTypes.waterQualitySample',
                })}-${dayjs().format('DD/MM/YYYY')}`,
              },
            },
            footer: {
              summaryValues: summary,
            } as DataGridSummaryProps,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'pondId', sort: 'asc' }],
            },
          }}
          onCellEditStop={handleEditing}
          onRowClick={({ id }) => {
            const { field } = apiRef.current?.state.tabIndex.cell ?? {};
            apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
          }}
        />
      </Box>
      {!readOnly && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={updateWaterQuality.isPending || disableSubmit || isEditing || hasError}
            onClick={handleSubmit}
            startIcon={updateWaterQuality.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditWaterQuality);
